import React, { useState, useEffect, useMemo } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Code, 
  Brain, 
  Database, 
  Globe, 
  Smartphone, 
  Server, 
  GitBranch, 
  Cloud,
  Cpu,
  Terminal,
  Layout,
  Zap,
  Star,
  TrendingUp
} from 'lucide-react';

const Skills = () => {
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
    rootMargin: '50px 0px',
  });

  const [hoveredSkill, setHoveredSkill] = useState(null);
  const [clickedSkill, setClickedSkill] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const controls = useAnimation();

  const skillsData = useMemo(() => [
    // Programming Languages
    { name: "Python", level: 90, icon: <Code />, color: "#3776ab", description: "Data Science & Backend", category: "Programming" },
    { name: "JavaScript", level: 85, icon: <Code />, color: "#f7df1e", description: "Frontend & Full Stack", category: "Programming" },
    { name: "TypeScript", level: 80, icon: <Code />, color: "#3178c6", description: "Type-safe Development", category: "Programming" },
    { name: "Java", level: 75, icon: <Code />, color: "#ed8b00", description: "Enterprise Applications", category: "Programming" },
    { name: "C++", level: 70, icon: <Code />, color: "#00599c", description: "System Programming", category: "Programming" },
    
    // AI/ML & Data Science
    { name: "TensorFlow", level: 85, icon: <Brain />, color: "#ff6f00", description: "Deep Learning Framework", category: "AI/ML" },
    { name: "PyTorch", level: 80, icon: <Brain />, color: "#ee4c2c", description: "Neural Networks", category: "AI/ML" },
    { name: "Scikit-learn", level: 85, icon: <Brain />, color: "#f7931e", description: "Machine Learning", category: "AI/ML" },
    { name: "Pandas", level: 90, icon: <Database />, color: "#150458", description: "Data Analysis", category: "AI/ML" },
    { name: "NumPy", level: 90, icon: <Cpu />, color: "#013243", description: "Numerical Computing", category: "AI/ML" },
    { name: "OpenCV", level: 75, icon: <Brain />, color: "#5c3ee8", description: "Computer Vision", category: "AI/ML" },
    
    // Web Development
    { name: "React.js", level: 80, icon: <Layout />, color: "#61dafb", description: "Frontend Framework", category: "Web" },
    { name: "Node.js", level: 75, icon: <Server />, color: "#68a063", description: "Backend Runtime", category: "Web" },
    { name: "Express.js", level: 70, icon: <Server />, color: "#404040", description: "Web Framework", category: "Web" },
    { name: "Next.js", level: 75, icon: <Layout />, color: "#000000", description: "Full Stack Framework", category: "Web" },
    { name: "Vue.js", level: 70, icon: <Layout />, color: "#4fc08d", description: "Progressive Framework", category: "Web" },
    
    // Mobile Development
    { name: "React Native", level: 75, icon: <Smartphone />, color: "#61dafb", description: "Cross-platform Mobile", category: "Mobile" },
    { name: "Flutter", level: 70, icon: <Smartphone />, color: "#02569b", description: "Mobile UI Framework", category: "Mobile" },
    
    // Database & Backend
    { name: "MongoDB", level: 75, icon: <Database />, color: "#47a248", description: "NoSQL Database", category: "Database" },
    { name: "PostgreSQL", level: 80, icon: <Database />, color: "#336791", description: "Relational Database", category: "Database" },
    { name: "MySQL", level: 85, icon: <Database />, color: "#4479a1", description: "SQL Database", category: "Database" },
    { name: "Firebase", level: 80, icon: <Database />, color: "#ffca28", description: "BaaS Platform", category: "Database" },
    
    // Tools & DevOps
    { name: "Git", level: 90, icon: <GitBranch />, color: "#f05032", description: "Version Control", category: "Tools" },
    { name: "Docker", level: 70, icon: <Cloud />, color: "#0db7ed", description: "Containerization", category: "Tools" },
    { name: "AWS", level: 65, icon: <Cloud />, color: "#ff9900", description: "Cloud Services", category: "Tools" },
    { name: "Linux", level: 85, icon: <Terminal />, color: "#fcc624", description: "Operating System", category: "Tools" },
    { name: "Jupyter", level: 90, icon: <Code />, color: "#f37626", description: "Data Science IDE", category: "Tools" },
    { name: "VS Code", level: 95, icon: <Code />, color: "#007acc", description: "Code Editor", category: "Tools" },
  ], []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [inView, controls]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      scale: 0.8,
      rotateX: -15
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      rotateX: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
        mass: 1
      }
    }
  };

  const handleSkillClick = (skillName) => {
    setClickedSkill(skillName);
    setTimeout(() => setClickedSkill(null), 2000);
  };

  return (
    <motion.section
      id="skills"
      className="section skills-section"
      ref={ref}
      initial={{ opacity: 0 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6 }}
    >
      <motion.h2
        className="section-title"
        initial={{ y: 50, opacity: 0 }}
        animate={inView ? { y: 0, opacity: 1 } : { y: 50, opacity: 0 }}
        transition={{ delay: 0.1, duration: 0.6 }}
      >
        <motion.span 
          className="title-icon"
          animate={{ rotate: inView ? [0, 360] : 0 }}
          transition={{ duration: 2, ease: "easeInOut" }}
        >
          <Zap />
        </motion.span>
        Skills & Technologies
      </motion.h2>

      <motion.div
        className="skills-container"
        variants={containerVariants}
        initial="hidden"
        animate={controls}
      >
        <div className="skills-grid">
          {skillsData.map((skill, index) => (
            <motion.div
              key={skill.name}
              className={`skill-card-interactive ${hoveredSkill === skill.name ? 'hovered' : ''} ${clickedSkill === skill.name ? 'clicked' : ''}`}
              variants={itemVariants}
              whileHover={{ 
                scale: 1.05,
                y: -12,
                rotateY: 8,
                transition: { 
                  type: "spring",
                  stiffness: 300,
                  damping: 20
                }
              }}
              whileTap={{ 
                scale: 0.95,
                rotate: [0, -2, 2, 0],
                transition: { duration: 0.3 }
              }}
              onHoverStart={() => setHoveredSkill(skill.name)}
              onHoverEnd={() => setHoveredSkill(null)}
              onClick={() => handleSkillClick(skill.name)}
            >
              {/* Magnetic Field Effect */}
              <div className="magnetic-field" />
              
              {/* Floating Particles */}
              <div className="floating-particles">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="particle"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={hoveredSkill === skill.name ? {
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                      x: [0, Math.random() * 100 - 50],
                      y: [0, Math.random() * 100 - 50],
                    } : {}}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.2,
                      ease: "easeInOut"
                    }}
                    style={{ backgroundColor: skill.color }}
                  />
                ))}
              </div>

              {/* Skill Content */}
              <div className="skill-content">
                <div className="skill-header">
                  <motion.div 
                    className="skill-icon-container"
                    whileHover={{ 
                      rotate: [0, -15, 15, 0],
                      scale: 1.2,
                      transition: { duration: 0.5 }
                    }}
                  >
                    <div className="skill-icon" style={{ color: skill.color }}>
                      {skill.icon}
                    </div>
                    <div className="icon-glow" style={{ backgroundColor: skill.color }} />
                  </motion.div>
                  
                  <div className="skill-info">
                    <h3>{skill.name}</h3>
                    <p className="skill-description">{skill.description}</p>
                    <div className="skill-badge">
                      <span className="category-badge">{skill.category}</span>
                      <div className="skill-rating">
                        {[...Array(5)].map((_, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0.3 }}
                            animate={{ 
                              opacity: i < Math.floor(skill.level / 20) ? 1 : 0.3,
                              scale: hoveredSkill === skill.name && i < Math.floor(skill.level / 20) ? 1.2 : 1
                            }}
                            transition={{ delay: i * 0.1 }}
                          >
                            <Star size={12} fill={i < Math.floor(skill.level / 20) ? skill.color : 'none'} color={skill.color} />
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="skill-level-container">
                  <div className="level-wrapper">
                    <span className="level-label">
                      <TrendingUp size={14} />
                      Proficiency
                    </span>
                    <motion.span 
                      className="level-percentage"
                      animate={hoveredSkill === skill.name ? {
                        scale: [1, 1.1, 1],
                        color: [skill.color, '#ffffff', skill.color]
                      } : {}}
                      transition={{ duration: 0.5 }}
                    >
                      {skill.level}%
                    </motion.span>
                  </div>
                  
                  <div className="progress-container">
                    <div className="progress-track" />
                    <motion.div
                      className="progress-bar"
                      style={{ backgroundColor: skill.color }}
                      initial={{ scaleX: 0 }}
                      animate={inView ? { scaleX: skill.level / 100 } : { scaleX: 0 }}
                      transition={{ 
                        delay: 0.3 + index * 0.03,
                        duration: 1.5,
                        ease: "easeOut"
                      }}
                    />
                    <motion.div
                      className="progress-pulse"
                      style={{ backgroundColor: skill.color }}
                      animate={hoveredSkill === skill.name ? {
                        scaleX: [0, skill.level / 100, 0],
                        opacity: [0, 0.8, 0]
                      } : {}}
                      transition={{ 
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Click Ripple Effect */}
              {clickedSkill === skill.name && (
                <motion.div
                  className="click-ripple"
                  initial={{ scale: 0, opacity: 0.8 }}
                  animate={{ scale: 3, opacity: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  style={{ backgroundColor: skill.color }}
                />
              )}

              {/* Hover Spotlight */}
              <motion.div
                className="hover-spotlight"
                style={{ 
                  background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, ${skill.color}20, transparent 50%)`
                }}
                animate={{
                  opacity: hoveredSkill === skill.name ? 0.5 : 0
                }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          ))}
        </div>
      </motion.div>
    </motion.section>
  );
};

export default Skills;
